import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { UserManagementService } from 'src/app/contract/shared/user-management.service';
import { Userrole } from 'src/app/contract/shared/userrole.model';
import { UserService } from 'src/app/shared/user.service';
import { Users } from 'src/app/shared/users.model';
import { DateAdapter } from '@angular/material/core';
import { Onboarding } from 'src/app/shared/onboarding.model';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}


@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('fileInput') fileInput: ElementRef;

  id: string;
  mode: string;
  userForm: any = FormGroup;
  organizationForm: any = FormGroup;
  selectedImage: string | ArrayBuffer | null = null;
  fileToUpload: File = null;
  matcher = new MyErrorStateMatcher();
  isLoading: boolean = false;
  hidePassword = true;
  roles: any;
  users: Users[];
  userData: Users;
  formLoading: boolean = false;
  base64Image = null;
  countries = [
    {
      id: 1,
      name: 'INDIA'
    }, {
      id: 2,
      name: 'UNITED KINGDOM'
    }, {
      id: 3,
      name: 'UNITED STATES'
    }, {
      id: 4,
      name: 'GERMANY'
    }, {
      id: 5,
      name: 'FRANCE'
    }, {
      id: 6,
      name: 'ITALY'
    },
  ];

  constructor(public usermanagementService: UserManagementService, public userService: UserService, private router: Router, private route: ActivatedRoute, private formBuilder: FormBuilder, private toast: ToastrService, private dateAdapter: DateAdapter<Date>) {
    this.dateAdapter.setLocale('en-GB');

    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.mode = params['mode'];
    });

    if (this.mode)
      this.formLoading = true;

    this.userForm = this.formBuilder.group({
      employeeId: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      middleName: [''],
      lastName: ['', [Validators.required]],
      emailAddress: ['', [Validators.email, Validators.required]],
      password: [''],
      address: [''],
      contactNumber: [null, [Validators.required]],
      countryId: [null, [Validators.required]],
      tenantId: [null],
      status: [null]

    });

    this.organizationForm = this.formBuilder.group({
      organizationLevel: ['', [Validators.required]],
      reportingManagerId: [null, [Validators.required]],
      reportingManager: [null],
      roleName: ['', [Validators.required]],
      dateOfJoining: ['', [Validators.required]],
      designation: ['', [Validators.required]],
      department: ['', [Validators.required]],
    });

  }

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  async ngOnInit(): Promise<void> {
    if (this.mode == 'view') {
      this.userForm.disable();
      this.organizationForm.disable();
    } else {
      this.userForm.enable();
      this.organizationForm.enable();
    }

    await this.usermanagementService.getAllRoles().then((res) => {
      this.roles = res;
    })
    await this.usermanagementService.getAllUsers().then((res: Users[]) => {
      this.users = res.filter(user => user.id != parseInt(this.id));
    })

    if (this.id) {

      await this.usermanagementService.getAUser(this.id).then((res: Users) => {
        this.userData = res;
      });

      await this.usermanagementService.getRolesOfAUser(this.id).then((role: Userrole[]) => {
        this.userData.roleId = role[0]?.userRoleId.roleId
      });

      this.userForm.patchValue(this.userData);
      this.organizationForm.patchValue(this.userData);

      // this.userForm.get('status').setValue(this.userData.status === 1 ? 'Active' : 'Inactive');

      if (this.userData.dateOfJoining)
        this.organizationForm.get('dateOfJoining').setValue(new Date(this.userData.dateOfJoining));

      const selectedCountry = this.countries.find(option => option.id === this.userData.countryId);
      if (selectedCountry)
        this.userForm.get('countryId').setValue(selectedCountry.id);

      const selectedRole = this.roles.find(option => option.id === this.userData.roleId);
      if (selectedRole)
        this.organizationForm.get('roleName').setValue(selectedRole.id);

      const selectedManager = this.users.find(option => option.id === this.userData.reportingManagerId);
      if (selectedManager)
        this.organizationForm.get('reportingManagerId').setValue(selectedManager.id);

      this.formLoading = false;
      this.selectedImage = null;
    } else {
      this.userForm.get('password').setValidators(Validators.required);
      this.userForm.get('password').updateValueAndValidity();
    }

    if (this.userData?.profilePic)
      this.base64Image = `data:image/png;base64,${this.userData.profilePic}`;

  }

  reset() {
    this.userForm.reset();
    this.organizationForm.reset();
    if (this.stepper) {
      this.stepper.reset();
    }
  }

  selectedReportingManager(event) {
    this.organizationForm.get('reportingManagerId').setValue(event.value)
    const selectedManager = this.users.find(option => option.id === event.value);
    this.organizationForm.get('reportingManager').setValue(selectedManager?.firstName + ' ' + selectedManager?.lastName)
    
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  // Method to handle file selection
  async onFileSelected(event) {
    this.isLoading = true;
    const file: File = event.target.files[0];
    if (file) {
      if (this.id) {
        try {
          await this.usermanagementService.updateUserImage(file, this.userData.id).then((res: any) => {
            this.base64Image = `data:image/png;base64,${res.image_base64}`;
            if (this.userService.loggedinUser.id == this.userData.id) {
              this.usermanagementService.getAUser(this.userData.id.toString()).then((res: Users) => {
                this.userService.loggedinUser = res;
              })
            }
            this.toast.success('The image has been updated successfully!', '');
          })
        } catch {
          this.toast.error('Failed to upload the image!', '');
        }
      } else {
        this.fileToUpload = file;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          this.selectedImage = reader.result;
        };
      }
    }
    this.isLoading = false;
  }

  async addUser(activate = false) {
    if (this.id) {
      if (this.userForm.valid && this.organizationForm.valid) {
        this.isLoading = true;
        const userData = { ...this.userForm.value, ...this.organizationForm.value }
        userData.id = parseInt(this.id);
        try {
          await this.usermanagementService.updateUser(userData).then((res: Users) => {
            let userRole: Userrole[] = [{
              userRoleId: {
                userId: res.id,
                roleId: parseInt(userData.roleName)
              },
              tenantId: res.tenantId
            }];
            if (this.userService.loggedinUser.id == res.id) {
              this.userService.loggedinUser = res;
            }
            this.usermanagementService.updateUserRole(res.id, userRole).then((r) => {
              this.reset();
              this.isLoading = false;
              this.toast.success('The user has been updated succesfully.', '');
              this.router.navigate(["/app/user/list"]);
            })
          })
        } catch {
          this.toast.error('Failed to update the user!', '');
          this.isLoading = false;
        }
      }
    } else {
      if (this.userForm.valid && this.organizationForm.valid) {
        this.isLoading = true;
        const userData = { ...this.userForm.value, ...this.organizationForm.value }
        try {
          await this.usermanagementService.createUser(userData, activate, this.fileToUpload).then((res: any) => {

            let userRole: Userrole[] = [{
              userRoleId: {
                userId: res.id,
                roleId: parseInt(res.roleName)
              },
              tenantId: res.tenantId
            }];

            this.usermanagementService.createUserRole(userRole).then((r) => {
              if (res.status = 1) {
                this.usermanagementService.sendMail(res.emailAddress);
              }
              this.reset();
              this.isLoading = false;
              this.toast.success('The user has been created succesfully.', '');
              this.router.navigate(["/app/user/list"]);
            })
          })
        } catch {
          this.toast.error('Failed to create the user!', '');
          this.isLoading = false;
        }
      }
    }
  }
}
